import { ProductBacklogDataManager } from "Agile/Scripts/Backlog/ProductBacklogDataManager";
import * as AgileProductBacklogResources from "Agile/Scripts/Resources/TFS.Resources.AgileProductBacklog";
import { CoreFieldRefNames } from "Presentation/Scripts/TFS/Generated/TFS.WorkItemTracking.Constants";
import { ParentWorkItemDataProviderData, ParentWorkItem } from "TFS/WorkItemTracking/DataProviderContracts";
import { getDefaultWebContext } from "VSS/Context";
import { WebPageDataService } from "VSS/Contributions/Services";
import { Debug } from "VSS/Diag";
import { publishErrorToTelemetry } from "VSS/Error";
import { getService } from "VSS/Service";

export const UnparentedId = -1;

export enum LoadingStatus {
    Loading = 0,
    Loaded = 1,
    Error = 2
}

export interface IParentWorkItemDataProvider {
    /**
     * Returns the loading status of the parent work item for the provided id.
     * @param childWorkItemId Child work item id to check if parent is loading.
     */
    getLoadingStatus(childWorkItemId: number): LoadingStatus;

    /**
     * Returns the error message recorded for the provided id;
     * @param childWorkItemId Child work item id to return the error message for.
     */
    getErrorMessage(childWorkItemId: number): string;
    
    /**
     * Returns the parent work item object for the provided id.
     * @param childWorkItemId Child work item id to return the parent of.
     */
    getParentWorkItem(childWorkItemId: number): ParentWorkItem | undefined;
    
    /**
     * @param childWorkItemIds Array of child work item ids to load parent information for.
     * @param parentId (Optional) Id of parent work item for all children if known (for example, from re-parent operation).
     */
    loadParentWorkItems(childWorkItemIds: number[], parentId?: number): Promise<void>;

    /**
     * Return the map of parent work items
     */
    getParentWorkItems(): IPromise<ParentWorkItem[]>;
}

export interface IPagedWorkItemDataSource {
    isWorkItemPaged(id: number): boolean;
    getValue(id: number, fieldRefName: string): any;
}

export class ParentWorkItemDataProvider implements IParentWorkItemDataProvider {
    private _childIdToParentIdMap: { [childId: number]: number };
    private _parentWorkItemMap: { [parentId: number]: ParentWorkItem | undefined };
    private _childIdToParentErrorMap: { [childId: number]: string };
    private _loadingPromise: IPromise<void>;

    private _productBacklogDataManager: ProductBacklogDataManager;
    private _pagedDataSource: IPagedWorkItemDataSource;

    constructor(productBacklogDataManager: ProductBacklogDataManager, pagedDataSource: IPagedWorkItemDataSource) {
        this._childIdToParentIdMap = {};
        this._parentWorkItemMap = {};
        this._childIdToParentErrorMap = {};
        this._loadingPromise = Promise.resolve();

        this._productBacklogDataManager = productBacklogDataManager;
        this._pagedDataSource = pagedDataSource;
    }

    public getLoadingStatus(childWorkItemId: number): LoadingStatus {
        if(this._childIdToParentErrorMap[childWorkItemId]){
            return LoadingStatus.Error
        }

        if(this._childIdToParentIdMap[childWorkItemId]) {
            return LoadingStatus.Loaded;
        }

        return LoadingStatus.Loading;
    }

    public getErrorMessage(childWorkItemId: number): string | undefined {
        return this._childIdToParentErrorMap[childWorkItemId];
    }

    public getParentWorkItem(childWorkItemId: number): ParentWorkItem | undefined {
        const parentId = this._childIdToParentIdMap[childWorkItemId];

        Debug.assertIsNumber(parentId, "parentId");

        if (!parentId || parentId === UnparentedId) {
            return undefined;
        }

        return this._parentWorkItemMap[parentId];
    }

    public async loadParentWorkItems(childWorkItemIds: number[], providedParentId?: number) {
        const workItemsToLoadParents: number[] = [];
        for (const id of childWorkItemIds) {
            if (id <= 0) {
                // ignore temporary or invalid ids
                continue;
            }

            // clear existing mapping.
            this._childIdToParentIdMap[id] = undefined;

            // use the parentId provided or try get from the ProductBacklogDataManager
            let parentId = providedParentId;
            if (!parentId) {
                parentId = this._productBacklogDataManager.getParentIdFromWorkItemId(id, /*preferRealParentId*/ true);
                
                // Ignore UnparentedId from productBacklogDataManager, as that just means unparented as far as it knows.
                // We should call the DP for this item to be sure.
                if (parentId === UnparentedId) {
                    parentId = undefined;
                }
            }
            
            // if we have a valid parent id try find the parent info from the map or page data source, otherwise we need to load from the data provider
            if (parentId) {
                const parentWorkItem = this._parentWorkItemMap[parentId];
                if (parentWorkItem) {
                    // this parent is known - add mapping to it
                    this._childIdToParentIdMap[id] = parentId;
                } else if (this._pagedDataSource.isWorkItemPaged(parentId)) {
                    // this parent is not known, try get from grid page data
                    this._parentWorkItemMap[parentId] = {
                        id: parentId,
                        fields: {
                            [CoreFieldRefNames.Id]: this._pagedDataSource.getValue(parentId, CoreFieldRefNames.Id),
                            [CoreFieldRefNames.Title]: this._pagedDataSource.getValue(parentId, CoreFieldRefNames.Title),
                            [CoreFieldRefNames.TeamProject]: this._pagedDataSource.getValue(parentId, CoreFieldRefNames.TeamProject),
                            [CoreFieldRefNames.WorkItemType]: this._pagedDataSource.getValue(parentId, CoreFieldRefNames.WorkItemType)
                        }
                    } as ParentWorkItem;
                    this._childIdToParentIdMap[id] = parentId;
                } else {
                    // unable to find parent data, need to load
                    workItemsToLoadParents.push(id);
                }
            } else {
                // unable to find parent id, need to load
                workItemsToLoadParents.push(id);
            }
        }
        if (workItemsToLoadParents && workItemsToLoadParents.length > 0) {
            this._loadingPromise = this._loadParentWorkItemsFromDataProvider(workItemsToLoadParents);
            await this._loadingPromise;
        }
    }

    public getParentWorkItems(): IPromise<ParentWorkItem[]> {
        return this._loadingPromise.then(() => {
            return Object.keys(this._parentWorkItemMap).map(k => this._parentWorkItemMap[k]);
        });
    }

    private async _loadParentWorkItemsFromDataProvider(childWorkItemIds: number[]) {
        try {
            const service = getService(WebPageDataService, getDefaultWebContext());
            const dataProviderId = "ms.vss-work-web.parent-workitem-data-provider";
            const requestProperties = {
                childIds: childWorkItemIds
            };
            const result: ParentWorkItemDataProviderData = await service.getDataAsync<ParentWorkItemDataProviderData>(
                dataProviderId,
                /*serviceInstanceId*/ null,
                requestProperties
            );
            if (result) {
                const { parentWorkItems, childToParentMap } = result;

                parentWorkItems.forEach(parent => {
                    this._parentWorkItemMap[parent.id] = parent;
                });

                childWorkItemIds.forEach(childId => {
                    const parentId = childToParentMap[childId];
                    this._childIdToParentIdMap[childId] = parentId ? parentId : UnparentedId;
                });
            } else {
                // data provider returns no result when there are no parents
                childWorkItemIds.forEach(childId => {
                    this._childIdToParentIdMap[childId] = UnparentedId;
                });
            }
        } catch (error) {
            childWorkItemIds.forEach(childId => {
                this._childIdToParentErrorMap[childId] = AgileProductBacklogResources.LoadParentErrorMessage;
            });

            error.name = "Error loading parent work items from data provider";
            publishErrorToTelemetry(error);
        }
    }
}
